import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

const PharmaDashboard = () => {
  // Mock Data
  const totalMedicines = 1200;
  const lowStockItems = [
    { name: "Paracetamol", stock: 8 },
    { name: "Amoxicillin", stock: 5 },
    { name: "Ibuprofen", stock: 12 },
  ];
  const recentSales = 15400;
  const topSelling = [
    { name: "Paracetamol", y: 300 },
    { name: "Cough Syrup", y: 250 },
    { name: "Vitamin C", y: 200 },
    { name: "Antibiotics", y: 180 },
    { name: "Painkillers", y: 150 },
    { name: "Supplements", y: 120 },
    { name: "Allergy Medicines", y: 100 },
  ];
  const pendingOrders = [
    { supplier: "ABC Pharma", items: 120, status: "Pending" },
    { supplier: "MediCorp", items: 200, status: "Shipped" },
  ];

  // Card Data
  const cardData = [
    {
      title: "Total Medicines",
      value: totalMedicines,
      description: "In current stock",
      bg: "#E0E7FF",
    },
    {
      title: "Recent Sales",
      value: `₹${recentSales}`,
      description: "From this month",
      bg: "#D1FAE5",
    },
    {
      title: "Top-Selling Medicines",
      value: topSelling.length,
      description: "Categories leading",
      bg: "#F3E8FF",
    },
    {
      title: "Pending Orders",
      value: pendingOrders.length,
      description: "Awaiting delivery",
      bg: "#FFE4E6",
    },
  ];

  // Highcharts Options
  const salesTrendOptions = {
    chart: { type: "line" },
    title: { text: "Recent Sales Trend" },
    credits: { enabled: false },
    xAxis: { categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] },
    yAxis: { title: { text: "Revenue" } },
    series: [
      { name: "Sales", data: [1200, 1900, 3000, 2500, 2200, 2900, 3200], color: "#2563eb" },
    ],
  };

  const purchaseVsSaleOptions = {
    chart: { type: "column" },
    title: { text: "Purchase vs Sale" },
    credits: { enabled: false },
    xAxis: { categories: ["Week 1", "Week 2", "Week 3", "Week 4"] },
    yAxis: { title: { text: "Amount" } },
    series: [
      { name: "Purchase", data: [5000, 7000, 6000, 8000], color: "#22c55e" },
      { name: "Sale", data: [4500, 6500, 5800, 7500], color: "#3b82f6" },
    ],
  };

  const topSellingOptions = {
    chart: { type: "pie" },
    title: { text: "Top Selling Medicines" },
    credits: { enabled: false },
    plotOptions: {
      pie: {
        innerSize: "60%", // makes it a donut
        dataLabels: {
          enabled: true,
          format: "{point.name}: {point.y}",
        },
      },
    },
    series: [
      {
        name: "Units Sold",
        colorByPoint: true,
        data: topSelling.map((item, idx) => ({
          ...item,
          color: Highcharts.getOptions().colors[idx % Highcharts.getOptions().colors.length],
        })),
      },
    ],
  };

  return (
    <div className="p-6 space-y-6">
      {/* Row 1: Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {cardData.map((card, idx) => (
          <div
            key={idx}
            className="rounded-2xl shadow-sm p-6 flex flex-col justify-between transform transition duration-300 hover:scale-105 hover:shadow-lg cursor-pointer"
            style={{ backgroundColor: card.bg }}
          >
            <h3 className="text-sm font-medium text-gray-700">{card.title}</h3>
            <p className="text-3xl font-extrabold mt-2 text-gray-900">{card.value}</p>
            <p className="text-xs text-gray-600 mt-1">{card.description}</p>
          </div>
        ))}
      </div>

      {/* Row 2: Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <HighchartsReact highcharts={Highcharts} options={salesTrendOptions} />
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <HighchartsReact highcharts={Highcharts} options={purchaseVsSaleOptions} />
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <HighchartsReact highcharts={Highcharts} options={topSellingOptions} />
        </div>
      </div>

      {/* Row 3: Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Low Stock Items */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Low Stock Items</h3>
          <ul className="divide-y divide-gray-100">
            {lowStockItems.map((item, idx) => (
              <li
                key={idx}
                className={`flex justify-between items-center py-3 px-4 rounded-xl mb-2 transition ${
                  item.stock < 10
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-gray-50 text-gray-700 border border-gray-200"
                } hover:shadow-md`}
              >
                <span className="font-medium">{item.name}</span>
                <span className="text-sm font-semibold">{item.stock} left</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pending Orders */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Pending Orders</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse rounded-xl overflow-hidden shadow-sm">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-800 text-left">
                  <th className="p-3 font-semibold">Supplier</th>
                  <th className="p-3 font-semibold">Items</th>
                  <th className="p-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingOrders.map((order, idx) => (
                  <tr
                    key={idx}
                    className="odd:bg-gray-50 even:bg-white hover:bg-indigo-50 transition"
                  >
                    <td className="p-3 font-medium text-gray-800">{order.supplier}</td>
                    <td className="p-3 text-gray-600">{order.items}</td>
                    <td
                      className={`p-3 font-semibold ${
                        order.status === "Pending"
                          ? "text-yellow-600"
                          : "text-green-600"
                      }`}
                    >
                      {order.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PharmaDashboard;
